import {
  saveReferral,
  fetchReferral,
  saveReferralAzure,
  fetchReferralAzure,
  fetchAzureTeamMembers,
  AzureTeamMember,
  fetchAzureReferralSolutions,
  AzureReferralSolution,
} from '../../../api/CoSell/index.ts'
import { camelize, snakeize } from 'casing'

import { AppDispatch, RootState } from '../../../../store/index.ts'
import { ReferralMetadata } from '../reducer.ts'
import { errorLogger } from '../../../../common/utils/errorLogger.ts'
import { AxiosError } from 'axios'
import { APIReferralError } from '../../../../externalApps/api/errorHandlers.ts'
import { removeEmptyValues } from '../../../../common/utils/CoSellHelpers/helperFunctions.ts'
import { isEmpty } from 'lodash'
import { ReferralMetadataType } from '../types.ts'
import { ActionType } from '../../../../externalApps/modules/referrals/actions.ts'
import {
  ReferralAzureFormSections,
  ReferralLeadFormSectionName,
  CustomerDetails,
  DealDetails,
  SolutionsOffered,
  HelpFromMicrosoft,
  PartialAzureFormObject,
  ReferralAzureForm,
} from './types.ts'
import azureReferralMockData from '../../../../mocks/azureReferralMockData.json'
import azureReferralSolutionsMockData from '../../../../mocks/azureReferralSolutionsMockData.json'
import { CloudType } from '../../../../common/modules/types'
type AzureReferralOppSections = {
  customerDetails?: {
    customerName?: string
    website?: string
    city?: string
    state?: string
    country?: string
    postalCode?: string
    firstName?: string
    lastName?: string
    fullName?: string
    email?: string
    phone?: number
    address?: string
  }
  dealDetails?: {
    dealType?: string
    dealName?: string
    location?: string
    currency?: string
    estimatedValue?: number
    estimatedCloseDate?: string
    crmId?: number
    marketingCampaignID?: string
    solutionPlay?: string
    solutionArea?: string
    notes?: string
    customerPurchaseIntent?: string
    teamMembers?: string[]
    contactDetails?: Array<{
      id: string
      firstName: string
      lastName: string
      phone: string
      email: string
    }>
  }
  solutionsOffered?: {
    companySolutions?: any[]
    microsoftSolutions?: any[]
    selectedSolutions?: string[]
  }
  helpFromMicrosoft?: {
    typeOfHelp?: string
    sellerDealView?: 'YES' | 'NO'
  }
}

export enum AzureFormActions {
  SET_AZURE_FORM = 'AZURE_SET_FORM',
  UPDATE_AZURE_FORM = 'AZURE_UPDATE_FORM',
  SET_AZURE = 'AZURE_SET',
  SET_FORM_AZURE_ERRORS = 'AZURE_SET_FORM_ERRORS',
  CLEAR_AZURE_FORM = 'AZURE_CLEAR_FORM',
  SET_AZURE_TEAM_MEMBERS = 'AZURE_SET_TEAM_MEMBERS',
  SET_AZURE_SOLUTIONS = 'AZURE_SET_SOLUTIONS',
  SET_AZURE_STATUS = 'AZURE_SET_STATUS',
}

export const setAzureCreationData = (
  onboardingFormData: Partial<ReferralAzureFormSections>
) => ({
  type: AzureFormActions.SET_AZURE_FORM,
  payload: onboardingFormData,
})

export const clearAzureForm = () => ({
  type: AzureFormActions.CLEAR_AZURE_FORM,
})

export type AzureChangeType = {
  sectionName: ReferralLeadFormSectionName
  data:
    | Partial<CustomerDetails>
    | Partial<DealDetails>
    | Partial<SolutionsOffered>
    | Partial<HelpFromMicrosoft>
}

export const setAzureDetails = (referral: PartialAzureFormObject) => ({
  type: AzureFormActions.SET_AZURE,
  payload: referral,
})

export const updateAzureCreationData = (data: AzureChangeType) => ({
  type: AzureFormActions.UPDATE_AZURE_FORM,
  payload: data,
})

export const emptyAzureFormFieldErrors = {} as Record<
  keyof ReferralAzureForm,
  string
>

export const setAzureFormFieldErrors = (
  errors: Record<keyof ReferralAzureForm, string>
) => ({
  type: AzureFormActions.SET_FORM_AZURE_ERRORS,
  payload: errors,
})

// API format for solutions (when sending/receiving in details.requirements.solutions)
type ApiSolutionFormat = {
  id: string
  type: string
  name: string
  price?: number
  currency?: string
  quantity?: number
  solutionType?: string
  publisherName: string
}

// Convert from display format (AzureReferralSolution) to API format
export const mapSolutionToApiFormat = (
  solution: AzureReferralSolution,
  additionalData?: { price?: number; currency?: string; quantity?: number }
): ApiSolutionFormat => {
  // Map solution_type to solutionType
  const solutionTypeMap: Record<string, string> = {
    COMPANY_SOLUTION: 'Company',
    MICROSOFT_SOLUTION: 'Microsoft',
    OTHER_THIRD_PARTY_SOLUTION: 'ThirdParty',
  }

  return {
    id: solution.id,
    type: 'Name', // Default type as shown in mock
    name: solution.solution_name,
    price: additionalData?.price,
    currency: additionalData?.currency,
    quantity: additionalData?.quantity,
    solutionType:
      solutionTypeMap[solution.solution_type] || solution.solution_type,
    publisherName: solution.publisher_name,
  }
}

// Convert from API format to display format (AzureReferralSolution)
export const mapSolutionFromApiFormat = (
  apiSolution: ApiSolutionFormat
): AzureReferralSolution | null => {
  // Map solutionType back to solution_type
  const solutionTypeReverseMap: Record<
    string,
    'COMPANY_SOLUTION' | 'MICROSOFT_SOLUTION' | 'OTHER_THIRD_PARTY_SOLUTION'
  > = {
    Company: 'COMPANY_SOLUTION',
    Microsoft: 'MICROSOFT_SOLUTION',
    ThirdParty: 'OTHER_THIRD_PARTY_SOLUTION',
    Cloud: 'MICROSOFT_SOLUTION', // fallback for "Cloud"
  }

  const mappedType = solutionTypeReverseMap[apiSolution.solutionType || '']
  if (!mappedType) {
    // If we can't determine the type, return null
    return null
  }

  return {
    id: apiSolution.id,
    solution_id: apiSolution.id, // Use id as solution_id
    solution_name: apiSolution.name,
    publisher_name: apiSolution.publisherName,
    solution_type: mappedType,
  }
}

export const mapAzureFormToApi = (
  referral:
    | Partial<AzureReferralOppSections>
    | Partial<ReferralAzureFormSections>
): {
  direction: string
  referralDetails: {
    objects: {
      name?: string
      customerProfile?: {
        name?: string
        address?: {
          city?: string
          state?: string
          country?: string
          postalCode?: string
          address?: string
        }
        team?: Array<{
          firstName?: string
          lastName?: string
          phoneNumber?: string
          email?: string
        }>
      }
      status?: string
      substatus?: string
      salesStage?: string
      details?: {
        dealValue?: number
        currency?: string
        closingDateTime?: string
      }
      consent?: {
        consentToContact?: boolean
        consentToShareReferralWithMicrosoftSellers?: boolean
      }
      externalReferenceId?: string
      type?: string
      team?: Array<{
        firstName?: string
        lastName?: string
        phoneNumber?: string
        email?: string
      }>
      target?: string[]
    }
    oppsyncFields?: Record<string, any>
    parentObjectKey?: string
  }
} => {
  let referralData: Partial<ReferralAzureFormSections>
  const formData = referral as Partial<AzureReferralOppSections>
  if (
    formData.solutionsOffered &&
    'selectedSolutions' in formData.solutionsOffered
  ) {
    referralData = transformFormToActionType(formData)
  } else {
    referralData = referral as Partial<ReferralAzureFormSections>
  }

  const { customerDetails, dealDetails, solutionsOffered, helpFromMicrosoft } =
    referralData

  const address: Record<string, any> = {}
  if (customerDetails?.city) address.city = customerDetails.city
  if (customerDetails?.state) address.state = customerDetails.state
  if (customerDetails?.country) address.country = customerDetails.country
  if (customerDetails?.postalCode)
    address.postalCode = customerDetails.postalCode
  if (customerDetails?.address) address.address = customerDetails.address

  const customerProfile: Record<string, any> = {}
  if (customerDetails?.customerName)
    customerProfile.name = customerDetails.customerName
  if (Object.keys(address).length > 0) customerProfile.address = address

  const details: Record<string, any> = {}
  if (dealDetails?.estimatedValue !== undefined) {
    details.dealValue = dealDetails.estimatedValue
  }
  if (dealDetails?.currency) details.currency = dealDetails.currency
  if (dealDetails?.estimatedCloseDate) {
    details.closingDateTime = dealDetails.estimatedCloseDate
  }
  const consent: Record<string, any> = {}
  if (helpFromMicrosoft?.sellerDealView !== undefined) {
    consent.consentToShareReferralWithMicrosoftSellers =
      helpFromMicrosoft.sellerDealView === 'YES'
  }
  consent.consentToContact = true

  const objects: Record<string, any> = {}
  if (dealDetails?.dealName) objects.name = dealDetails.dealName
  if (Object.keys(customerProfile).length > 0) {
    objects.customerProfile = customerProfile
  }
  if (dealDetails?.dealType) objects.type = dealDetails.dealType
  if (dealDetails?.crmId) {
    objects.externalReferenceId = dealDetails.crmId
  }
  const contactDetails = (dealDetails as any)?.contactDetails
  if (
    contactDetails &&
    Array.isArray(contactDetails) &&
    contactDetails.length > 0
  ) {
    // Filter out empty team members (all fields are empty) and map to API format
    objects.team = contactDetails
      .filter((contact: any) => {
        // Include team member if at least one field has a non-empty value
        return (
          (contact.firstName && contact.firstName.trim() !== '') ||
          (contact.lastName && contact.lastName.trim() !== '') ||
          (contact.email && contact.email.trim() !== '') ||
          (contact.phone && contact.phone.trim() !== '')
        )
      })
      .map((contact: any) => ({
        firstName: contact.firstName || '',
        lastName: contact.lastName || '',
        phoneNumber: contact.phone || '',
        email: contact.email || '',
      }))
  }
  if (
    solutionsOffered?.solutionsOffered &&
    solutionsOffered.solutionsOffered.length > 0
  ) {
    objects.target = solutionsOffered.solutionsOffered
  }
  if (Object.keys(details).length > 0) objects.details = details
  if (Object.keys(consent).length > 0) objects.consent = consent

  const oppsyncFields: Record<string, any> = {}
  if (customerDetails?.website) oppsyncFields.website = customerDetails.website
  if (customerDetails?.email) oppsyncFields.email = customerDetails.email
  if ((customerDetails?.phone?.replace(/\D/g, '').length ?? 0) > 3)
    oppsyncFields.phone = customerDetails?.phone
  if (customerDetails?.firstName)
    oppsyncFields.firstName = customerDetails.firstName
  if (customerDetails?.lastName)
    oppsyncFields.lastName = customerDetails.lastName
  if (customerDetails?.fullName)
    oppsyncFields.fullName = customerDetails.fullName
  if (dealDetails?.location) oppsyncFields.location = dealDetails.location
  if (dealDetails?.marketingCampaignID) {
    oppsyncFields.marketingCampaignID = dealDetails.marketingCampaignID
  }
  if (dealDetails?.solutionArea)
    oppsyncFields.solutionArea = dealDetails.solutionArea
  if (dealDetails?.solutionPlay)
    oppsyncFields.solutionPlay = dealDetails.solutionPlay
  if (dealDetails?.customerPurchaseIntent) {
    oppsyncFields.customerPurchaseIntent = dealDetails.customerPurchaseIntent
  }
  if (dealDetails?.partnerNotes)
    oppsyncFields.partnerNotes = dealDetails.partnerNotes
  if (helpFromMicrosoft?.typeOfHelp) {
    oppsyncFields.typeOfHelp = helpFromMicrosoft.typeOfHelp
  }
  if (helpFromMicrosoft?.notesToMicrosoft) {
    oppsyncFields.notesToMicrosoft = helpFromMicrosoft.notesToMicrosoft
  }

  return {
    direction: 'CRM_TO_MPC',
    referralDetails: {
      objects,
      oppsyncFields: Object.keys(oppsyncFields).length > 0 ? oppsyncFields : {},
      parentObjectKey: dealDetails?.crmId || '',
    },
  }
}

export const mapAzureApiToForm = (
  values: any
): Partial<ReferralAzureFormSections> => {
  const camelizedValues = camelize(values)

  if (camelizedValues?.referralDetails) {
    const { referralDetails } = camelizedValues
    const { objects, oppsyncFields } = referralDetails || {}

    if (!objects) {
      return {}
    }

    const customerDetails: Partial<CustomerDetails> = {}
    if (objects.customerProfile?.name) {
      customerDetails.customerName = objects.customerProfile.name
    }
    if (objects.customerProfile?.address) {
      const addr = objects.customerProfile.address
      if (addr.city) customerDetails.city = addr.city
      if (addr.state) customerDetails.state = addr.state
      if (addr.country) customerDetails.country = addr.country
      if (addr.postalCode) customerDetails.postalCode = addr.postalCode
      if (addr.address) customerDetails.address = addr.address
    }
    if (oppsyncFields) {
      if (oppsyncFields.website) customerDetails.website = oppsyncFields.website
      if (oppsyncFields.email) customerDetails.email = oppsyncFields.email
      if (oppsyncFields.phone) customerDetails.phone = oppsyncFields.phone
      if (oppsyncFields.firstName)
        customerDetails.firstName = oppsyncFields.firstName
      if (oppsyncFields.lastName)
        customerDetails.lastName = oppsyncFields.lastName
      if (oppsyncFields.fullName)
        customerDetails.fullName = oppsyncFields.fullName
    }

    const dealDetails: Partial<DealDetails> & { contactDetails?: any[] } = {}
    if (objects.name) dealDetails.dealName = objects.name
    if (objects.type) dealDetails.dealType = objects.type
    if (objects.externalReferenceId)
      dealDetails.crmId = objects.externalReferenceId
    if (
      objects.team &&
      Array.isArray(objects.team) &&
      objects.team.length > 0
    ) {
      dealDetails.contactDetails = objects.team.map(
        (member: any, index: number) => ({
          id: member.id || `${Math.floor(Math.random() * 10000)}-${index}`,
          firstName: member.firstName || '',
          lastName: member.lastName || '',
          phone: member.phoneNumber || member.phone || '',
          email: member.email || '',
        })
      )
      // Pre-select team members by setting their email addresses
      dealDetails.teamMembers = objects.team
        .map((member: any) => member.email)
        .filter((email: string) => !!email)
    }
    if (objects.details) {
      if (objects.details.dealValue !== undefined) {
        dealDetails.estimatedValue = objects.details.dealValue
      }
      if (objects.details.currency)
        dealDetails.currency = objects.details.currency
      if (objects.details.closingDateTime) {
        dealDetails.estimatedCloseDate = objects.details.closingDateTime
      }
    }
    if (oppsyncFields) {
      if (oppsyncFields.location) dealDetails.location = oppsyncFields.location
      if (oppsyncFields.marketingCampaignID) {
        dealDetails.marketingCampaignID = oppsyncFields.marketingCampaignID
      }
      if (oppsyncFields.solutionArea)
        dealDetails.solutionArea = oppsyncFields.solutionArea
      if (oppsyncFields.solutionPlay)
        dealDetails.solutionPlay = oppsyncFields.solutionPlay
      if (oppsyncFields.customerPurchaseIntent) {
        dealDetails.customerPurchaseIntent =
          oppsyncFields.customerPurchaseIntent
      }
      if (oppsyncFields.partnerNotes)
        dealDetails.partnerNotes = oppsyncFields.partnerNotes
    }

    const solutionsOffered: Partial<SolutionsOffered> & {
      solutionsOffered?: string[]
      apiSolutions?: ApiSolutionFormat[]
    } = {}
    if (objects.target) {
      solutionsOffered.solutionsOffered = objects.target
    }

    // Handle solutions from details.requirements.solutions (API format)
    // Store both IDs and full API solution objects for later use in component
    if (
      objects.details?.requirements &&
      objects.details.requirements.solutions &&
      Array.isArray(objects.details.requirements.solutions)
    ) {
      // Store API solution objects with price, quantity, currency
      solutionsOffered.apiSolutions = objects.details.requirements.solutions

      // Convert API format solutions and store as IDs
      const solutionIds = objects.details.requirements.solutions
        .map((apiSol: any) => {
          const converted = mapSolutionFromApiFormat(apiSol)
          return converted?.id || converted?.solution_id || apiSol.id
        })
        .filter((id: string) => !!id)

      if (solutionIds.length > 0) {
        solutionsOffered.solutionsOffered = [
          ...(solutionsOffered.solutionsOffered || []),
          ...solutionIds,
        ].filter((id, index, self) => self.indexOf(id) === index) // Remove duplicates
      }
    }

    const helpFromMicrosoft: Partial<HelpFromMicrosoft> = {}
    if (
      objects.consent?.consentToShareReferralWithMicrosoftSellers !== undefined
    ) {
      // Map boolean to 'YES' | 'NO'
      helpFromMicrosoft.sellerDealView = objects.consent
        .consentToShareReferralWithMicrosoftSellers
        ? 'YES'
        : 'NO'
    }
    if (oppsyncFields) {
      if (oppsyncFields.typeOfHelp) {
        helpFromMicrosoft.typeOfHelp = oppsyncFields.typeOfHelp
      }
      if (oppsyncFields.notesToMicrosoft) {
        helpFromMicrosoft.notesToMicrosoft = oppsyncFields.notesToMicrosoft
      }
    }

    return {
      customerDetails:
        Object.keys(customerDetails).length > 0 ? customerDetails : undefined,
      dealDetails:
        Object.keys(dealDetails).length > 0 ? dealDetails : undefined,
      solutionsOffered:
        Object.keys(solutionsOffered).length > 0 ? solutionsOffered : undefined,
      helpFromMicrosoft:
        Object.keys(helpFromMicrosoft).length > 0
          ? helpFromMicrosoft
          : undefined,
    } as Partial<ReferralAzureFormSections>
  }

  return camelizedValues as Partial<ReferralAzureFormSections>
}

const transformFormToActionType = (
  formData: Partial<AzureReferralOppSections>
): Partial<ReferralAzureFormSections> => {
  const transformed: Partial<ReferralAzureFormSections> = {}

  if (formData.customerDetails) {
    transformed.customerDetails = {
      customerName: formData.customerDetails.customerName,
      website: formData.customerDetails.website,
      city: formData.customerDetails.city,
      state: formData.customerDetails.state,
      country: formData.customerDetails.country,
      postalCode: formData.customerDetails.postalCode,
      firstName: formData.customerDetails.firstName,
      lastName: formData.customerDetails.lastName,
      fullName: formData.customerDetails.fullName,
      email: formData.customerDetails.email,
      phone: formData.customerDetails.phone?.toString(),
      address: formData.customerDetails.address,
    }
  }

  if (formData.dealDetails) {
    transformed.dealDetails = {
      dealType: formData.dealDetails.dealType,
      dealName: formData.dealDetails.dealName,
      location: formData.dealDetails.location,
      currency: formData.dealDetails.currency,
      estimatedValue: formData.dealDetails.estimatedValue,
      estimatedCloseDate: formData.dealDetails.estimatedCloseDate,
      crmId: formData.dealDetails.crmId?.toString(),
      marketingCampaignID: formData.dealDetails.marketingCampaignID,
      solutionArea: formData.dealDetails.solutionArea,
      solutionPlay: formData.dealDetails.solutionPlay,
      customerPurchaseIntent: formData.dealDetails.customerPurchaseIntent,
      partnerNotes: formData.dealDetails.notes,
    } as DealDetails & { contactDetails?: any }
    if (formData.dealDetails.contactDetails) {
      ;(transformed.dealDetails as any).contactDetails =
        formData.dealDetails.contactDetails
    }
  }

  if (formData.solutionsOffered) {
    // Preserve the full solutionsOffered structure with companySolutions and microsoftSolutions
    // to maintain price and quantity data
    transformed.solutionsOffered = {
      companySolutions: formData.solutionsOffered.companySolutions || [],
      microsoftSolutions: formData.solutionsOffered.microsoftSolutions || [],
      selectedSolutions: formData.solutionsOffered.selectedSolutions || [],
    } as any
  }

  if (formData.helpFromMicrosoft) {
    transformed.helpFromMicrosoft = {
      typeOfHelp: formData.helpFromMicrosoft.typeOfHelp,
      sellerDealView: formData.helpFromMicrosoft.sellerDealView,
      notesToMicrosoft: undefined,
    }
  }

  return transformed
}

export const submitAzureForm =
  (
    referral:
      | Partial<AzureReferralOppSections>
      | Partial<ReferralAzureFormSections>,
    status: ReferralMetadata['referralStatus'] = 'submitted',
    referralId?: string,
    actionType?: ActionType,
    crmUserIdFromUrl?: string
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const partnerData = getState().AccountData.user.partnerData
    const referralFromBackend =
      getState().leadReferralForm.referral?.referralObject
    const { awsLeadStatus, acceptanceStatus, leadStatusReason } =
      getState().leadReferralForm.referralForm.leadDetails

    let referralData: Partial<ReferralAzureFormSections>
    if ('customerDetails' in referral && referral.customerDetails) {
      const formData = referral as Partial<AzureReferralOppSections>
      if (
        formData.solutionsOffered &&
        'selectedSolutions' in formData.solutionsOffered
      ) {
        referralData = transformFormToActionType(formData)
      } else {
        referralData = referral as Partial<ReferralAzureFormSections>
      }
    } else {
      referralData = referral as Partial<ReferralAzureFormSections>
    }

    const referralFromForm = removeEmptyValues(referralData)
    const referralPayload: Partial<ReferralAzureFormSections> = {
      ...(referralFromBackend ? camelize(referralFromBackend) : {}),
      ...Object.fromEntries(
        Object.entries(referralFromForm).filter(
          ([key, value]) => value !== '' && value !== undefined
        )
      ),
    }

    if (leadStatusReason && referralPayload.dealDetails) {
      referralPayload.dealDetails.customerPurchaseIntent = leadStatusReason
    }

    let externalReferenceId: string | undefined = crmUserIdFromUrl

    if (!externalReferenceId && referralPayload.dealDetails?.crmId) {
      externalReferenceId = String(referralPayload.dealDetails.crmId)
    }

    if (!externalReferenceId) {
      externalReferenceId = partnerData?.crmId || ''
    }

    if (externalReferenceId && referralPayload.dealDetails) {
      referralPayload.dealDetails.crmId = externalReferenceId
    }

    const apiPayload = mapAzureFormToApi(referralPayload)

    // Convert solutions to API format and add to details.requirements.solutions
    const solutionsOffered = referralPayload.solutionsOffered as any
    if (solutionsOffered) {
      const availableSolutions = getState().azureReferralForm?.solutions || []
      const apiSolutions: ApiSolutionFormat[] = []

      // Handle new format with companySolutions and microsoftSolutions arrays
      if (
        solutionsOffered.companySolutions &&
        Array.isArray(solutionsOffered.companySolutions)
      ) {
        solutionsOffered.companySolutions.forEach((sol: any) => {
          const solution = availableSolutions.find(
            (s: AzureReferralSolution) =>
              s.id === sol.solutionId || s.solution_id === sol.solutionId
          )
          if (solution) {
            apiSolutions.push(mapSolutionToApiFormat(solution))
          }
        })
      }

      if (
        solutionsOffered.microsoftSolutions &&
        Array.isArray(solutionsOffered.microsoftSolutions)
      ) {
        solutionsOffered.microsoftSolutions.forEach((sol: any) => {
          const solution = availableSolutions.find(
            (s: AzureReferralSolution) =>
              s.id === sol.solutionId || s.solution_id === sol.solutionId
          )
          if (solution) {
            apiSolutions.push(
              mapSolutionToApiFormat(solution, {
                price: sol.price,
                quantity: sol.quantity,
                currency: referralPayload.dealDetails?.currency || 'USD',
              })
            )
          }
        })
      }

      // Handle old format with solutionsOffered array (for backward compatibility)
      if (
        solutionsOffered.solutionsOffered &&
        Array.isArray(solutionsOffered.solutionsOffered) &&
        apiSolutions.length === 0
      ) {
        solutionsOffered.solutionsOffered.forEach((solutionId: string) => {
          const solution = availableSolutions.find(
            (s: AzureReferralSolution) =>
              s.id === solutionId || s.solution_id === solutionId
          )
          if (solution) {
            apiSolutions.push(mapSolutionToApiFormat(solution))
          }
        })
      }

      if (
        apiSolutions.length > 0 &&
        apiPayload.referralDetails.objects.details
      ) {
        const details = apiPayload.referralDetails.objects.details as any
        if (!details.requirements) {
          details.requirements = {}
        }
        details.requirements.solutions = apiSolutions
      }
    }

    const referralPayloadForApi = snakeize(apiPayload)

    const state = getState()
    const tenantId = state.tenants?.selectedTenantId
    const partnerId = tenantId || partnerData?.partnerId || ''
    const crmId = partnerData?.crmId || ''

    try {
      const { data } = await saveReferralAzure(
        referralPayloadForApi,
        partnerId,
        referralId,
        'hubspot'
      )

      const camelizedData = camelize(data)
      const newReferralId = (camelizedData as any)?.referralId || referralId
      const lastReferralId = referralId ? referralId : newReferralId

      const responseFormData = mapAzureApiToForm(camelizedData)

      const newReferralObject: Partial<ReferralAzureFormSections> = {
        customerDetails: {
          ...referralPayload.customerDetails,
          ...responseFormData.customerDetails,
        },
        dealDetails: {
          ...referralPayload.dealDetails,
          ...responseFormData.dealDetails,
        },
        solutionsOffered: {
          ...referralPayload.solutionsOffered,
          ...responseFormData.solutionsOffered,
        },
        helpFromMicrosoft: {
          ...referralPayload.helpFromMicrosoft,
          ...responseFormData.helpFromMicrosoft,
        },
      }

      const apiPayloadForStorage = mapAzureFormToApi(newReferralObject)

      await dispatch(
        setAzureDetails({
          referralMetadata: {
            crmId: crmId,
            referralType: 'referral_opportunity' as ReferralMetadataType,
            referralStatus: status,
            crmParentTable: 'labra_referral',
            crmPrimaryKeyName: 'id',
            ...(!isEmpty(lastReferralId) && { referralId: newReferralId }),
            ...(!isEmpty(lastReferralId) && {
              crmPrimaryKeyValue: newReferralId,
            }),
          },
          referralObject: snakeize(apiPayloadForStorage) as any,
          referralObjectTransformed: newReferralObject,
        })
      )
    } catch (e: any) {
      const error = e as AxiosError<APIReferralError>

      const globalState = getState()
      errorLogger({ globalState })(error as Error)
      return { error: e }
    }
  }

export const setAzureTeamMembers = (teamMembers: AzureTeamMember[]) => ({
  type: AzureFormActions.SET_AZURE_TEAM_MEMBERS,
  payload: teamMembers,
})

export const getAzureTeamMembers =
  (
    partnerId: string,
    referralTeamMembers?: Array<{
      firstName?: string
      lastName?: string
      email?: string
      phoneNumber?: string
    }>
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      // Fetch team members from API
      const teamMembers = await fetchAzureTeamMembers(partnerId)

      // Merge with referral team members if provided
      let mergedTeamMembers: AzureTeamMember[] = [...teamMembers]

      if (referralTeamMembers && referralTeamMembers.length > 0) {
        referralTeamMembers.forEach(referralMember => {
          // Check if team member already exists (by email)
          const exists = mergedTeamMembers.some(
            member => member.email === referralMember.email
          )

          if (
            !exists &&
            referralMember.email &&
            referralMember.firstName &&
            referralMember.lastName
          ) {
            mergedTeamMembers.push({
              firstName: referralMember.firstName,
              lastName: referralMember.lastName,
              email: referralMember.email,
              phoneNumber: referralMember.phoneNumber,
            })
          }
        })
      }

      dispatch(setAzureTeamMembers(mergedTeamMembers))
    } catch (e: any) {
      const error = e as AxiosError<APIReferralError>
      const globalState = getState()
      errorLogger({ globalState })(error as Error)

      // If API fails, still set referral team members if available
      if (referralTeamMembers && referralTeamMembers.length > 0) {
        const referralMembers: AzureTeamMember[] = referralTeamMembers
          .filter(m => m.email && m.firstName && m.lastName)
          .map(m => ({
            firstName: m.firstName!,
            lastName: m.lastName!,
            email: m.email!,
            phoneNumber: m.phoneNumber,
          }))
        dispatch(setAzureTeamMembers(referralMembers))
      }
    }
  }

export const setAzureSolutions = (solutions: AzureReferralSolution[]) => ({
  type: AzureFormActions.SET_AZURE_SOLUTIONS,
  payload: solutions,
})

export const setAzureStatus = (status: string, substatus: string) => ({
  type: AzureFormActions.SET_AZURE_STATUS,
  payload: { status, substatus },
})

export const getAzureSolutions =
  (partnerId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      // Fetch solutions from API
      const solutions = await fetchAzureReferralSolutions(partnerId)
      dispatch(setAzureSolutions(solutions))
    } catch (e: any) {
      const error = e as AxiosError<APIReferralError>
      const globalState = getState()
      errorLogger({ globalState })(error as Error)

      // Fallback to mock data on error
      console.warn('Failed to fetch solutions from API, using mock data:', e)
      dispatch(
        setAzureSolutions(
          azureReferralSolutionsMockData as AzureReferralSolution[]
        )
      )
    }
  }

export const getAzureReferral =
  (partnerId: string, referralId: string, crmType: string = 'hubspot') =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const partnerData = getState().AccountData.user.partnerData
    const crmId = partnerData?.crmId || ''

    const actions = {
      setReferralDetails: setAzureDetails,
      setReferralCreationData: setAzureCreationData,
      transform: mapAzureApiToForm,
    }

    let referralData: any

    try {
      const { data } = await fetchReferralAzure(partnerId, referralId, crmType)
      referralData = data
    } catch (e: any) {
      const error = e as AxiosError<APIReferralError>
      const globalState = getState()
      errorLogger({ globalState })(error as Error)

      // Fallback to mock data on error
      console.warn('Failed to fetch referral from API, using mock data:', e)
      referralData = azureReferralMockData
    }

    // Wrap the response in the expected format for the transform function
    const apiResponse = referralData?.referralDetails
      ? camelize(referralData)
      : {
          referralDetails: {
            objects: camelize(referralData),
            oppsyncFields: {},
          },
        }

    const responseFormData = actions.transform(
      apiResponse
    ) as Partial<ReferralAzureFormSections>
    const referralIdFromResponse = referralData?.id || referralId

    // Set the form creation data (following the same pattern as getReferral)
    await dispatch(actions.setReferralCreationData(responseFormData as any))

    // Transform the form data back to API format for storage
    const apiPayloadForStorage = mapAzureFormToApi(responseFormData)

    await dispatch(
      actions.setReferralDetails({
        referralMetadata: {
          crmId: crmId,
          referralType: 'referral_opportunity' as ReferralMetadataType,
          referralStatus: 'submitted',
          crmParentTable: 'labra_referral',
          crmPrimaryKeyName: 'id',
          ...(!isEmpty(referralIdFromResponse) && {
            referralId: referralIdFromResponse,
          }),
          ...(!isEmpty(referralIdFromResponse) && {
            crmPrimaryKeyValue: referralIdFromResponse,
          }),
        },
        referralObject: snakeize(apiPayloadForStorage) as any,
        referralObjectTransformed: responseFormData,
      })
    )

    // Fetch and merge team members
    const referralTeamMembers = [
      ...(referralData?.team || []),
      ...(referralData?.customerProfile?.team || []),
    ]
    await dispatch(getAzureTeamMembers(partnerId, referralTeamMembers))

    // Set status and substatus if available
    if (referralData?.status || referralData?.substatus) {
      dispatch(
        setAzureStatus(
          referralData?.status || '',
          referralData?.substatus || ''
        )
      )
    }
  }
